import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { Box } from "@mui/material"

import { GRID_LAYOUT_GAP } from "helpers/utils/constants"
import { getMapCenterFromDeviceLocation } from "helpers/utils/map"
import Map from "widgets/plots/geo/Map"
import { PlotContainer } from "widgets/styled/containers"
import SectionTitle from "widgets/common/SectionTitle"
import DeviceMapMarkers from "./DeviceMapMarkers"
import { useDeviceLocationData } from "./useDeviceLocationData"

interface GpsLocationPlotProps {
  deviceId: number
  mapHeight: string
}

const GpsLocationPlot = ({ deviceId, mapHeight }: GpsLocationPlotProps) => {
  const { t } = useTranslation()
  const { locationData: formattedLocationData, hasLocationData } =
    useDeviceLocationData(deviceId)

  const center = useMemo(
    () => getMapCenterFromDeviceLocation(formattedLocationData),
    [formattedLocationData],
  )

  // The key is necessary for the map to rerender and update its height when mapHeight changes
  const mapKey = useMemo(
    () => `map-location-${deviceId}-${mapHeight}`,
    [deviceId, mapHeight],
  )

  if (!hasLocationData || !formattedLocationData) {
    return null
  }

  return (
    <PlotContainer
      maxWidth="xl"
      type={"geo"}
      sx={{
        display: "flex",
        flexDirection: "column",
        mt: `${GRID_LAYOUT_GAP}px`,
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: 1,
          mb: 1,
        }}
      >
        <SectionTitle title={t(`plot_titles.LOCATION`)} />
      </Box>
      <Map key={mapKey} center={center} mapHeight={mapHeight}>
        <DeviceMapMarkers devices={formattedLocationData} />
      </Map>
    </PlotContainer>
  )
}

export default GpsLocationPlot
